import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import UserAvatar from './UserAvatar';
import { DemandDetailModal } from './DemandDetailModal';
import ContactDetailPanel from './ContactDetailPanel';
import CityModalMobile from './CityModalMobile';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CityElectionResult {
  ano: number;
  votos_obtidos: number;
  meta_votos: number | null;
}

export interface CityModalData {
  id: number;
  nome: string;
  uf: string;
  ibge_id?: number | null;
  regiao?: string | null;
  mesorregiao?: string | null;
  populacao?: number | null;
  populacao_votantes?: number | null;
  populacao_ano?: number | null;
  responsavel?: { id: number; nome: string; foto_url?: string | null } | null;
  projecao_votos?: number | null;
  electionHistory?: CityElectionResult[];
  ultima_visita?: string | null;
  multiplicadores?: Array<{ id: number; nome: string; foto_url?: string | null }>;
  notas_estrategicas?: string | null;
  data_importacao?: string | null;
}

export interface CityModalRegistro {
  id: number;
  texto?: string;
  descricao_atualizacao?: string;
  tipo?: 'comentario' | 'historico';
  data?: string;
  created_at?: string;
  autor?: { id: number; nome: string; foto_url?: string | null };
  usuario?: { id: number; nome: string };
}

export interface CityModalDemanda {
  id: number;
  titulo: string;
  status: 'NAO_INICIADA' | 'EM_ANDAMENTO' | 'PAUSADA' | 'CONCLUIDA' | 'CANCELADA';
  data_cadastro: string;
  solicitante?: { nome: string };
}

export interface CityModalContato {
  id: number;
  nome: string;
  cargo?: string;
  foto_url?: string | null;
  is_multiplicador?: boolean;
  whatsapp?: string;
}

export interface CityModalProps {
  isOpen: boolean;
  onClose: () => void;
  city: CityModalData | null;
  teamMembers?: Array<{
    id: number;
    nome: string;
    tipo_usuario: string;
    nivel_acesso: string;
    foto_url?: string | null;
  }>;
  onSave: (cityId: number, data: {
    electionHistory?: CityElectionResult[];
    projecao_votos?: number;
    deletedElectionYears?: number[];
    notas_estrategicas?: string;
  }) => Promise<void>;
  onAssignResponsible?: (cityId: number, userId: number) => Promise<void>;
  onScheduleVisit?: (cityId: number) => void;
  onNewDemand?: (cityId: number) => void;
  onExportReport?: (cityId: number) => void;
  onViewAllContacts?: (cityId: number) => void;
  onAddRegistro?: (cityId: number, texto: string) => Promise<void>;
  onAddMultiplicador?: (cityId: number) => void;
  onVincularContato?: (cityId: number) => void;
  onDemandClick?: (demand: CityModalDemanda) => void;
  onContatoClick?: (contato: CityModalContato) => void;
  demandPanel?: {
    isOpen: boolean;
    demand: CityModalDemanda | null;
    onClose: () => void;
    registros?: any[];
    loadingRegistros?: boolean;
    newRegistro?: string;
    onNewRegistroChange?: (v: string) => void;
    onAddRegistro?: () => void;
    addingRegistro?: boolean;
    editingRegistro?: number | null;
    editingText?: string;
    onEditRegistro?: (id: number) => void;
    onSaveEdit?: (id: number) => void;
    onCancelEdit?: () => void;
    onEditTextChange?: (v: string) => void;
    onDeleteRegistro?: (id: number) => void;
    savingRegistroId?: number | null;
    deletingRegistroId?: number | null;
    openMenuId?: number | null;
    onMenuToggle?: (id: number | null) => void;
    canViewUpdates?: boolean;
    canCreateUpdates?: boolean;
    canEditUpdate?: (r: any) => boolean;
    canDeleteUpdate?: (r: any) => boolean;
    canEditDemand?: boolean;
    canDeleteDemand?: boolean;
    canAssignResponsible?: boolean;
    canUnassignResponsible?: boolean;
    historico?: any[];
    loadingHistorico?: boolean;
    onDemandUpdated?: (demandId: number) => void;
  };
  registros?: CityModalRegistro[];
  demandas?: CityModalDemanda[];
  contatos?: CityModalContato[];
  loadingRegistros?: boolean;
  loadingDemandas?: boolean;
  loadingContatos?: boolean;
}

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const shimmerAnim = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const ShimmerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
`;
const ShimmerCircle = styled.div<{ $size?: number }>`
  width:  ${p => p.$size ?? 32}px;
  height: ${p => p.$size ?? 32}px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 800px 100%;
  animation: ${shimmerAnim} 1.4s infinite linear;
`;
const ShimmerBar = styled.div<{ $w?: string; $h?: string }>`
  width:  ${p => p.$w ?? '120px'};
  height: ${p => p.$h ?? '13px'};
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 800px 100%;
  animation: ${shimmerAnim} 1.4s infinite linear;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

// ─── Overlay / Shell ──────────────────────────────────────────────────────────

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.52);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${p => (p.$open ? 1 : 0)};
  pointer-events: ${p => (p.$open ? 'auto' : 'none')};
  transition: opacity 0.22s ease;

  @media (max-width: 900px) {
    display: none;
  }
`;

const Modal = styled.div<{ $open: boolean }>`
  background: #fff;
  border-radius: 18px;
  width: 92vw;
  max-width: 1180px;
  height: 760px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  box-shadow: 0 1px 1px rgba(16,24,40,.04), 0 12px 32px -8px rgba(16,24,40,.18), 0 24px 60px -12px rgba(16,24,40,.22);
  border: 1px solid #e5e7eb;
  transform: ${p => (p.$open ? 'scale(1) translateY(0)' : 'scale(0.96) translateY(8px)')};
  opacity: ${p => (p.$open ? 1 : 0)};
  transition: transform 0.28s cubic-bezier(0.2, 0.7, 0.2, 1), opacity 0.28s ease;

  @media (max-width: 900px) {
    width: 100%;
    max-width: 100%;
    height: 94vh;
    border-radius: 20px 20px 0 0;
    transform: ${p => (p.$open ? 'translateY(0)' : 'translateY(100%)')};
  }
`;

// ─── Header ───────────────────────────────────────────────────────────────────

const slideInPanel = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const slideOutPanel = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(100%); }
`;

const DemandSlidePanel = styled.div<{ $closing: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 10;
  border-radius: 18px;
  overflow: hidden;
  ${p => !p.$closing ? css`animation: ${slideInPanel} 0.32s cubic-bezier(0.32, 0, 0.15, 1) both;` : css`animation: ${slideOutPanel} 0.28s cubic-bezier(0.32, 0, 0.15, 1) both;`}
`;

const Header = styled.div`
  flex-shrink: 0;
  padding: 18px 22px 16px;
  border-bottom: 1px solid #e5e7eb;
  background:
    radial-gradient(60% 120% at 0% 0%, rgba(37,99,235,0.06), transparent 60%),
    #fff;
`;

const Breadcrumb = styled.div`
  font-size: 11.5px;
  font-weight: 600;
  color: #9ca3af;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Dot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #d1d5db;
  display: inline-block;
  flex-shrink: 0;
`;

const Tag = styled.span`
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  background: transparent;
  border-radius: 4px;
  font-size: 11px;
  color: #6b7280;
  letter-spacing: 0;
  text-transform: none;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TitleArea = styled.div`
  flex: 1;
  min-width: 0;
`;

const TitleDisplay = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TitleEdit = styled.input`
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  border: none;
  outline: none;
  background: rgba(var(--accent-rgb), 0.08);
  box-shadow: 0 0 0 2px rgba(var(--accent-rgb), 0.25);
  padding: 2px 8px;
  border-radius: 6px;
  width: 100%;
  box-sizing: border-box;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
`;

const Btn = styled.button<{ $v?: 'primary' | 'secondary' | 'ghost' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, transform 0.04s;
  white-space: nowrap;

  ${p =>
    p.$v === 'primary'
      ? `
    background: var(--accent, #2563eb); border: 1px solid var(--accent, #2563eb);
    color: white;
    &:hover { filter: brightness(0.88); }
    &:active { transform: translateY(1px); }
    &:disabled { background: #d1d5db; border-color: #d1d5db; cursor: not-allowed; }
  `
      : p.$v === 'ghost'
      ? `
    background: transparent; border: 1px solid transparent;
    color: #6b7280; padding: 0 8px;
    &:hover { background: #f8fafc; color: #111827; }
  `
      : `
    background: #fff; border: 1px solid #d1d5db;
    color: #111827;
    &:hover { background: #f8fafc; }
    &:active { transform: translateY(1px); }
  `}
`;

const CloseBtn = styled.button`
  width: 31px;
  height: 31px;
  border: none;
  background: #f3f4f6;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #6b7280;
  transition: all 0.14s;
  flex-shrink: 0;

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }
`;

const MoreWrap = styled.div`
  position: relative;
`;

const MoreDrop = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.11);
  min-width: 200px;
  z-index: 200;
  overflow: hidden;
  animation: ${fadeIn} 0.14s ease;
`;

const MoreItem = styled.button`
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 10px 14px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
  text-align: left;
  transition: background 0.1s;

  &:hover {
    background: #f3f4f6;
  }
`;

// ─── Two-column body ──────────────────────────────────────────────────────────

const Body = styled.div`
  display: grid;
  grid-template-columns: 1.35fr 1fr;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const LeftCol = styled.div`
  min-width: 0;
  overflow-y: auto;
  padding: 22px 26px 0;
  display: flex;
  flex-direction: column;
  gap: 22px;

  scrollbar-width: thin;
  scrollbar-color: #dfe1e6 transparent;
  &::-webkit-scrollbar { width: 10px; }
  &::-webkit-scrollbar-thumb { background: #dfe1e6; border: 3px solid #fff; border-radius: 999px; }
`;

const RightCol = styled.div`
  border-left: 1px solid #e5e7eb;
  background: #fff;
  display: grid;
  grid-template-rows: auto 1fr auto;
  min-height: 0;

  @media (max-width: 900px) {
    display: none;
  }
`;

// ─── Card ─────────────────────────────────────────────────────────────────────

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: visible;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16,24,40,.04);
`;

const CardHead = styled.div`
  padding: 14px 18px 10px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const CardLabel = styled.h3`
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  margin: 0 0 2px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  display: inline-flex;
  align-items: center;
  gap: 7px;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent, #2563eb);
    box-shadow: 0 0 0 3px rgba(37,99,235,0.18);
    flex-shrink: 0;
  }
`;

const CardSub = styled.div`
  font-size: 12.5px;
  color: #9ca3af;
  line-height: 1.4;
`;

const CardBody = styled.div`
  padding: 14px 18px;
`;

// ─── Vote Chart ───────────────────────────────────────────────────────────────

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
`;

const Swatch = styled.div<{ $k: 'blue' | 'gray' | 'amber' }>`
  width: 18px;
  height: 11px;
  border-radius: 3px;
  flex-shrink: 0;
  ${p =>
    p.$k === 'blue'
      ? `background: linear-gradient(180deg, #60a5fa 0%, #2563eb 100%);`
      : p.$k === 'gray'
      ? `
        background-color: #e5e7eb;
        background-image: repeating-linear-gradient(
          -45deg,
          rgba(156,163,175,0.7) 0px, rgba(156,163,175,0.7) 4px,
          transparent 4px, transparent 10px
        );
        border: 1.5px solid #d1d5db;
      `
      : `
        background-color: #fde68a;
        background-image: repeating-linear-gradient(
          -45deg,
          rgba(251,191,36,0.75) 0px, rgba(251,191,36,0.75) 5px,
          rgba(253,230,138,0.4) 5px, rgba(253,230,138,0.4) 11px
        );
        border: 1.5px solid #fcd34d;
      `}
`;

const ChartScroll = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 24px;
  height: 280px;
  width: 100%;
  padding-bottom: 36px;
`;

const ChartCol = styled.div<{ $click?: boolean }>`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: flex-end;
  position: relative;
  cursor: ${p => (p.$click ? 'pointer' : 'default')};
  border-radius: 8px;
  padding: 0 2px;
  transition: background 0.13s;

  ${p => p.$click && `&:hover { background: rgba(37,99,235,0.06); }`}
`;

const Bars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 230px;
  width: 100%;
  justify-content: center;
  position: relative;
`;

const BarWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  flex: 1;
  min-width: 0;
`;

const BarVal = styled.span<{ $k?: 'result' | 'meta' | 'proj' }>`
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  line-height: 1;
  color: ${p => p.$k === 'result' ? 'var(--accent, #2563eb)' : p.$k === 'proj' ? '#b45309' : '#6b7280'};
`;

const Bar = styled.div<{ $h: number; $k: 'result' | 'meta' | 'proj' }>`
  width: 100%;
  border-radius: 5px 5px 0 0;
  flex-shrink: 0;
  ${p => {
    const h = p.$h > 0 ? Math.max(p.$h, 6) : 0;
    if (p.$k === 'result')
      return `
        height: ${h}px;
        background: linear-gradient(180deg, color-mix(in srgb, #2563eb 70%, white) 0%, #2563eb 100%);
        border-radius: 5px 5px 1px 1px;
      `;
    if (p.$k === 'meta')
      return `
        height: ${h}px;
        background: repeating-linear-gradient(135deg,
          rgba(107,114,128,0.18) 0 5px,
          transparent 5px 10px);
        border: 1px solid #d1d5db;
        border-radius: 5px 5px 1px 1px;
      `;
    // proj (current campaign / target)
    return `
      height: ${h}px;
      background: repeating-linear-gradient(135deg, #F5C44C 0 8px, #E8A33B 8px 16px);
      border-radius: 6px 6px 1px 1px;
    `;
  }}
`;

const NoMetaSlot = styled.div`
  width: 100%;
  height: 44px;
  border: 2px dashed #e5e7eb;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #9ca3af;
  transition: all 0.13s;
  white-space: nowrap;

  &:hover {
    border-color: var(--accent, #2563eb);
    color: var(--accent, #2563eb);
  }
`;

const ColLabel = styled.div`
  position: absolute;
  bottom: -32px;
  left: 0;
  right: 0;
  text-align: center;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11.5px;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.02em;
  line-height: 1.3;
`;

const ColStatus = styled.span<{ $ok: boolean | null }>`
  display: block;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${p => (p.$ok === true ? '#1FA060' : p.$ok === false ? '#D54343' : '#9ca3af')};
`;

const ChartFoot = styled.div<{ $click?: boolean }>`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #e5e7eb;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  ${p => p.$click && `cursor: pointer; &:hover .chf-edit { opacity: 1; }`}
`;

// ─── Popover ──────────────────────────────────────────────────────────────────

const Popover = styled.div`
  position: fixed;
  z-index: 4000;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
  padding: 15px;
  width: 224px;
  animation: ${fadeIn} 0.14s ease;
`;

const PopTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 11px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PopBadge = styled.span<{ $s: 'ok' | 'below' | 'none' }>`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
  ${p =>
    p.$s === 'ok'
      ? 'background:#d1fae5;color:#065f46;'
      : p.$s === 'below'
      ? 'background:#fee2e2;color:#991b1b;'
      : 'background:#f3f4f6;color:#6b7280;'}
`;

const PopField = styled.div`
  margin-bottom: 9px;
  &:last-of-type { margin-bottom: 0; }
`;

const PopLabel = styled.label`
  display: block;
  font-size: 10px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 3px;
`;

const PopInput = styled.input`
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 7px;
  font-size: 14px;
  color: #111827;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.13s, box-shadow 0.13s;

  &:focus {
    border-color: var(--accent, #2563eb);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`;

const PopActions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 11px;
`;

const PopBtn = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 6px 10px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.13s;
  ${p =>
    p.$primary
      ? 'background:var(--accent,#2563eb);color:white;&:hover{filter:brightness(1.07);}'
      : 'background:#f3f4f6;color:#6b7280;&:hover{background:#e5e7eb;}'}
`;

// ─── City Info ────────────────────────────────────────────────────────────────

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const InfoLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: #111827;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const UFBadge = styled.span`
  display: inline-block;
  background: #111827;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  letter-spacing: 0.04em;
`;

const Mono = styled.span`
  font-family: ui-monospace, monospace;
  font-size: 13px;
  color: #374151;
`;

// ─── Local team ───────────────────────────────────────────────────────────────

const TeamRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
  &:last-child { border-bottom: none; }
`;

const TeamKey = styled.span`
  font-size: 12px;
  color: #6b7280;
  min-width: 108px;
  flex-shrink: 0;
  padding-top: 2px;
`;

const TeamVal = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  flex-wrap: wrap;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #f3f4f6;
  border-radius: 20px;
  padding: 3px 10px 3px 4px;
  font-size: 12px;
  color: #374151;
`;

const AddChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px dashed #d1d5db;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 12px;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.13s;

  &:hover {
    border-color: var(--accent, #2563eb);
    color: var(--accent, #2563eb);
  }
`;

const RespBtn = styled.button<{ $has: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 4px;
  background: ${p => (p.$has ? 'rgba(var(--accent-rgb), 0.08)' : '#f9fafb')};
  border: 1px solid ${p => (p.$has ? 'rgba(var(--accent-rgb), 0.25)' : '#e5e7eb')};
  border-radius: 20px;
  font-size: 13px;
  color: ${p => (p.$has ? 'var(--accent)' : '#9ca3af')};
  cursor: pointer;
  transition: all 0.13s;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: rgba(var(--accent-rgb), 0.08);
  }
`;

const RespDrop = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.11);
  min-width: 220px;
  z-index: 300;
  overflow: hidden;
  animation: ${fadeIn} 0.14s ease;
`;

const RespOpt = styled.button`
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 10px 14px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: #111827;
  text-align: left;
  transition: background 0.1s;

  &:hover { background: #f3f4f6; }
`;

// ─── Notes ────────────────────────────────────────────────────────────────────

const NotesBox = styled.div<{ $active: boolean }>`
  min-height: 72px;
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid ${p => (p.$active ? 'var(--accent)' : 'transparent')};
  background: ${p => (p.$active ? 'rgba(var(--accent-rgb), 0.06)' : '#f9fafb')};
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
  outline: none;
  cursor: text;
  transition: all 0.13s;
  white-space: pre-wrap;
  word-break: break-word;

  &:not([contenteditable='true']):hover { background: #f3f4f6; }
  &:empty::before { content: attr(data-placeholder); color: #9ca3af; pointer-events: none; }
`;

const FootNote = styled.div`
  font-size: 11px;
  color: #d1d5db;
  text-align: center;
  padding: 4px 0 6px;
`;

// ─── Right panel ──────────────────────────────────────────────────────────────

const RTabs = styled.div`
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  padding: 0 14px;
  flex-shrink: 0;
`;

const RTab = styled.button<{ $a: boolean }>`
  padding: 10px 11px;
  font-size: 13px;
  font-weight: ${p => (p.$a ? 700 : 400)};
  color: ${p => (p.$a ? 'var(--accent,#2563eb)' : '#6b7280')};
  background: none;
  border: none;
  border-bottom: 2px solid ${p => (p.$a ? 'var(--accent,#2563eb)' : 'transparent')};
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.13s;
  white-space: nowrap;

  &:hover { color: var(--accent, #2563eb); }
`;

const RBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
`;

// Feed (Registros)

const FeedFilter = styled.div`
  display: flex;
  gap: 3px;
  padding: 9px 14px 7px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
`;

const FeedFilterBtn = styled.button<{ $a: boolean }>`
  padding: 4px 9px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: ${p => (p.$a ? 600 : 400)};
  color: ${p => (p.$a ? '#111827' : '#6b7280')};
  background: ${p => (p.$a ? 'white' : 'transparent')};
  border: ${p => (p.$a ? '1px solid #e5e7eb' : '1px solid transparent')};
  cursor: pointer;
  box-shadow: ${p => (p.$a ? '0 1px 3px rgba(0,0,0,0.05)' : 'none')};
  transition: all 0.11s;

  &:hover { color: #111827; }
`;

const FeedList = styled.div`
  flex: 1;
  padding: 5px 14px 10px;
  display: flex;
  flex-direction: column;
`;

const FeedItem = styled.div`
  display: flex;
  gap: 9px;
  padding: 9px 0;
  border-bottom: 1px solid #f9fafb;

  &:last-child { border-bottom: none; }
`;

const FeedContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const FeedMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex-wrap: wrap;
`;

const FeedAuthor = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #111827;
`;

const FeedTime = styled.span`
  font-size: 11px;
  color: #9ca3af;
  margin-left: 6px;
`;

const FeedTag = styled.span`
  font-size: 10px;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 4px;
  padding: 1px 5px;
  margin-left: 5px;
`;

const FeedText = styled.p`
  font-size: 13px;
  color: #374151;
  margin: 2px 0 0;
  line-height: 1.5;
`;

const Empty = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 13px;
  padding: 36px 0;
`;

const Composer = styled.div`
  flex-shrink: 0;
  padding: 9px 13px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const ComposerTA = styled.textarea`
  width: 100%;
  min-height: 58px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #111827;
  resize: none;
  outline: none;
  box-sizing: border-box;
  font-family: inherit;
  line-height: 1.5;
  transition: border-color 0.13s;

  &:focus { border-color: var(--accent, #2563eb); }
  &::placeholder { color: #9ca3af; }
`;

const SendBtn = styled.button`
  align-self: flex-end;
  padding: 6px 13px;
  background: var(--accent, #2563eb);
  color: white;
  border: none;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.13s;

  &:hover { filter: brightness(1.07); }
  &:disabled { background: #d1d5db; cursor: not-allowed; }
`;

// Demandas tab

const DCard = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  margin: 0 14px 7px;
  cursor: pointer;
  transition: box-shadow 0.13s;

  &:hover { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07); }
`;

const DStripe = styled.div<{ $s: string }>`
  width: 4px;
  flex-shrink: 0;
  background: ${p => {
    switch (p.$s) {
      case 'EM_ANDAMENTO': return '#2563eb';
      case 'PAUSADA': return '#f59e0b';
      case 'CONCLUIDA': return '#10b981';
      case 'CANCELADA': return '#ef4444';
      default: return '#9ca3af';
    }
  }};
`;

const DInfo = styled.div`
  flex: 1;
  padding: 8px 11px;
  min-width: 0;
`;

const DTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DMeta = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 3px;
  display: flex;
  gap: 5px;
  align-items: center;
  flex-wrap: wrap;
`;

const StatusPill = styled.span<{ $s: string }>`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  background: ${p => {
    switch (p.$s) {
      case 'EM_ANDAMENTO': return '#dbeafe';
      case 'PAUSADA': return '#fef3c7';
      case 'CONCLUIDA': return '#d1fae5';
      case 'CANCELADA': return '#fee2e2';
      default: return '#f3f4f6';
    }
  }};
  color: ${p => {
    switch (p.$s) {
      case 'EM_ANDAMENTO': return '#1e40af';
      case 'PAUSADA': return '#92400e';
      case 'CONCLUIDA': return '#065f46';
      case 'CANCELADA': return '#991b1b';
      default: return '#6b7280';
    }
  }};
`;

// Contatos tab

const CRow = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 14px;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.1s;

  &:hover { background: #f9fafb; }
  &:hover .ha { opacity: 1; }
  &:last-child { border-bottom: none; }
`;

const CInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CCargo = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 1px;
`;

const HoverActs = styled.div`
  display: flex;
  gap: 3px;
  opacity: 0;
  transition: opacity 0.13s;
`;

const IBtn = styled.button`
  width: 27px;
  height: 27px;
  border: none;
  background: #f3f4f6;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.13s;

  &:hover { background: #e5e7eb; color: #111827; }
`;

const TabAction = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 9px 14px 12px;
  padding: 8px 13px;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;
  width: calc(100% - 28px);
  justify-content: center;
  transition: all 0.13s;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: rgba(var(--accent-rgb), 0.08);
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

// ─── Section eyebrow + Fields layout ─────────────────────────────────────────

const SectionEyebrow = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 2px 2px 8px;
`;

const FieldsGrid = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  overflow: visible;
`;

const Field = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr;
  min-height: 40px;
  padding: 6px 8px 6px 4px;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  position: relative;
  transition: background 0.1s;

  &:first-child { border-radius: 10px 10px 0 0; }
  &:last-child { border-bottom: none; border-radius: 0 0 10px 10px; }
  &:only-child { border-radius: 10px; }
  &:hover { background: #f8fafc; }
  &:hover .field-edit { opacity: 1; }
`;

const FieldLbl = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding-left: 4px;
`;

const FieldVal = styled.span`
  font-size: 14px;
  color: #111827;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding-left: 16px;
`;

const FieldEditBtn = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  background: #f3f4f6;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  opacity: 0;
  transition: opacity 0.13s, background 0.1s;

  &:hover { background: #e5e7eb; color: #111827; }
`;

const MultList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const MultChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  padding: 3px 9px 3px 4px;
  font-size: 12px;
  color: #374151;
`;

const NotesEditable = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;
  min-height: 90px;
  font-size: 13.5px;
  line-height: 1.55;
  color: #374151;
  outline: none;
  transition: border-color 0.13s, box-shadow 0.13s;
  white-space: pre-wrap;
  word-break: break-word;

  &:focus {
    border-color: var(--accent, #2563eb);
    box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
  }
  &:empty::before {
    content: attr(data-placeholder);
    color: #9ca3af;
    font-style: italic;
    pointer-events: none;
  }
`;

const Danger = styled.div`
  padding: 10px 4px 18px;
`;

const DangerTxt = styled.div`
  font-size: 11px;
  color: #9ca3af;
  line-height: 1.5;
`;

// ─── Pane tabs (right col) ────────────────────────────────────────────────────

const PaneTabs = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 4px;
  background: #fff;
  flex-shrink: 0;
`;

const PaneTabBtn = styled.button<{ $a: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: ${p => (p.$a ? 600 : 400)};
  color: ${p => (p.$a ? 'var(--accent, #2563eb)' : '#6b7280')};
  background: none;
  border: none;
  border-bottom: 2px solid ${p => (p.$a ? 'var(--accent, #2563eb)' : 'transparent')};
  margin-bottom: -1px;
  cursor: pointer;
  transition: all 0.13s;
  white-space: nowrap;

  &:hover { color: var(--accent, #2563eb); }
`;

const PaneTabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #f3f4f6;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 700;
  color: #374151;
`;

// ─── Reg head + feed ──────────────────────────────────────────────────────────

const RegHead = styled.div`
  padding: 14px 22px 10px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
`;

const RegCount = styled.div`
  font-size: 11.5px;
  color: #9ca3af;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const RegFilter = styled.div`
  display: flex;
  gap: 2px;
`;

const RegFilterBtn = styled.button<{ $a: boolean }>`
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: ${p => (p.$a ? 600 : 400)};
  color: ${p => (p.$a ? '#111827' : '#6b7280')};
  background: ${p => (p.$a ? 'white' : 'transparent')};
  border: ${p => (p.$a ? '1px solid #e5e7eb' : '1px solid transparent')};
  cursor: pointer;
  box-shadow: ${p => (p.$a ? '0 1px 3px rgba(0,0,0,0.05)' : 'none')};
  transition: all 0.11s;

  &:hover { color: #111827; }
`;

const RegFeed = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px 4px;

  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
`;

const RegDay = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 10px 0 6px;
`;

const EvItem = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 10px;
  margin-bottom: 4px;
`;

const EvRail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const EvDot = styled.div<{ $comment?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${p => (p.$comment ? 'rgba(var(--accent-rgb), 0.08)' : '#f3f4f6')};
  border: 1px solid ${p => (p.$comment ? 'rgba(var(--accent-rgb), 0.25)' : '#e5e7eb')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => (p.$comment ? 'var(--accent)' : '#6b7280')};
  flex-shrink: 0;
`;

const EvBody = styled.div`
  flex: 1;
  min-width: 0;
  padding-top: 4px;
  padding-bottom: 10px;
`;

const EvHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 5px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #374151;
  line-height: 1.4;
`;

const EvWho = styled.span`
  font-weight: 700;
  color: #111827;
`;

const EvTime = styled.span`
  font-size: 11px;
  color: #9ca3af;
  margin-left: auto;
  white-space: nowrap;
`;

const EvBubble = styled.div`
  margin-top: 6px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px 12px 12px 4px;
  padding: 10px 12px;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
`;

// ─── Composer ─────────────────────────────────────────────────────────────────

const ComposerForm = styled.form`
  flex-shrink: 0;
  padding: 10px 14px 12px;
  border-top: 1px solid #e5e7eb;
`;

const ComposerBox = styled.div`
  border: 1px solid #d1d5db;
  border-radius: 12px;
  padding: 8px 10px;
  transition: border-color 0.13s, box-shadow 0.13s;

  &:focus-within {
    border-color: var(--accent, #2563eb);
    box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
  }
`;

const ComposerInput = styled.textarea`
  width: 100%;
  min-height: 36px;
  border: none;
  outline: none;
  font-size: 13.5px;
  color: #111827;
  resize: none;
  font-family: inherit;
  line-height: 1.5;
  background: transparent;
  box-sizing: border-box;

  &::placeholder { color: #9ca3af; }
`;

const ComposerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
`;

const ComposerTools = styled.div`
  display: flex;
  gap: 2px;
`;

const ComposerToolBtn = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.1s;

  &:hover { background: #f3f4f6; color: #374151; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtN = (n?: number | null) =>
  n == null ? '—' : n.toLocaleString('pt-BR');

const fmtD = (s?: string | null) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; }
};

const ago = (s?: string | null) => {
  if (!s) return '';
  const m = Math.floor((Date.now() - new Date(s).getTime()) / 60000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const SL: Record<string, string> = {
  NAO_INICIADA: 'Não Iniciada',
  EM_ANDAMENTO: 'Em Andamento',
  PAUSADA: 'Pausada',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};

interface PopState {
  ano: number;
  meta: string;
  result: string;
  x: number;
  y: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CityModal: React.FC<CityModalProps> = ({
  isOpen,
  onClose,
  city,
  teamMembers = [],
  onSave,
  onAssignResponsible,
  onScheduleVisit,
  onNewDemand,
  onExportReport,
  onViewAllContacts,
  onAddRegistro,
  onAddMultiplicador,
  onVincularContato,
  onDemandClick,
  onContatoClick,
  demandPanel,
  registros = [],
  demandas = [],
  contatos = [],
  loadingRegistros = false,
  loadingDemandas = false,
  loadingContatos = false,
}) => {
  const { isAdmin } = useAuth();
  const { can } = usePermissionCheck();
  const canEdit = can('cities.edit.election_results');
  const canView = can('cities.view.election_results');
  const canViewComments = can('cities.comments.view');
  const canComment = can('cities.comments.create');

  const TARGET = 2026;

  // title
  const [editTitle, setEditTitle] = useState(false);
  const [titleVal, setTitleVal] = useState('');

  // more menu
  const [showMore, setShowMore] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // elections
  const [elections, setElections] = useState<Array<{ ano: number; votos: string; meta: string }>>([]);
  const [proj, setProj] = useState('');
  const snap = useRef('');

  // popover
  const [pop, setPop] = useState<PopState | null>(null);
  const popRef = useRef<HTMLDivElement>(null);

  // responsible
  const [showResp, setShowResp] = useState(false);
  const [respQ, setRespQ] = useState('');
  const [assigningResp, setAssigningResp] = useState(false);
  const respRef = useRef<HTMLDivElement>(null);

  // notes
  const notesRef = useRef<HTMLDivElement>(null);
  const [editNotes, setEditNotes] = useState(false);

  // right panel
  const [rtab, setRtab] = useState<'comentarios' | 'demandas' | 'contatos'>('comentarios');
  const [isPanelClosing, setIsPanelClosing] = useState(false);
  const [openContatoId, setOpenContatoId] = useState<number | null>(null);
  const [isContactPanelClosing, setIsContactPanelClosing] = useState(false);

  const handleDemandPanelClose = () => {
    setIsPanelClosing(true);
    setTimeout(() => { setIsPanelClosing(false); demandPanel?.onClose(); }, 290);
  };

  const handleContatoPanelClose = () => {
    setIsContactPanelClosing(true);
    setTimeout(() => { setIsContactPanelClosing(false); setOpenContatoId(null); }, 290);
  };
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── init ──────────────────────────────────────────────────────────────────
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!city) return;
    setTitleVal(city.nome);
    setProj(city.projecao_votos?.toString() ?? '');
    const sorted = [...(city.electionHistory ?? [])].sort((a, b) => a.ano - b.ano);
    const mapped = sorted.map(e => ({
      ano: e.ano,
      votos: e.votos_obtidos?.toString() ?? '',
      meta: e.meta_votos?.toString() ?? '',
    }));
    setElections(mapped);
    snap.current = JSON.stringify(mapped);
    setRtab(canViewComments ? 'comentarios' : 'demandas');
    setPop(null);
    setEditTitle(false);
    setEditNotes(false);
    setShowMore(false);
    setComment('');
  }, [city]);

  // ── outside-click handlers ────────────────────────────────────────────────
  useEffect(() => {
    if (!pop) return;
    const h = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) savePop();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [pop]);

  useEffect(() => {
    if (!showMore) return;
    const h = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setShowMore(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showMore]);

  useEffect(() => {
    if (!showResp) return;
    const h = (e: MouseEvent) => {
      if (respRef.current && !respRef.current.contains(e.target as Node)) setShowResp(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showResp]);

  if (!city) return null;

  // ── chart math ────────────────────────────────────────────────────────────
  // If the most recent election has no results it is the ongoing campaign,
  // not a past result — show it as the target column, not a history bar.
  const lastEl = elections[elections.length - 1];
  const isCurrentCampaign = lastEl && !parseInt(lastEl.votos || '0');
  const currentCampaign = isCurrentCampaign ? lastEl : null;
  const pastElections = isCurrentCampaign ? elections.slice(0, -1) : elections;

  // The target year / value: prefer the ongoing campaign entry, fallback to TARGET const
  const targetYear = currentCampaign ? currentCampaign.ano : TARGET;
  const targetMeta = currentCampaign ? (currentCampaign.meta || proj) : proj;
  const targetMetaN = parseInt(targetMeta || '0');

  const allNums = pastElections
    .flatMap(e => [parseInt(e.votos) || 0, parseInt(e.meta) || 0])
    .concat(targetMetaN ? [targetMetaN] : []);
  const maxN = Math.max(...allNums, 1);
  const toH = (v: string | number | null | undefined) => {
    const n = typeof v === 'string' ? parseInt(v) : Number(v ?? 0);
    if (!n || isNaN(n)) return 0;
    return Math.round((n / maxN) * 210);
  };

  const metasBatidas = pastElections.filter(e => {
    const v = parseInt(e.votos || '0');
    const m = parseInt(e.meta || '0');
    return m > 0 && v >= m;
  }).length;
  const withMeta = pastElections.filter(e => e.meta).length;
  const yr0 = pastElections[0]?.ano;
  const yrN = currentCampaign ? currentCampaign.ano : pastElections[pastElections.length - 1]?.ano;
  const range = elections.length > 0 ? `${yr0}–${yrN}` : '';
  const peak = pastElections.reduce(
    (b, e) => (parseInt(e.votos || '0') > parseInt(b?.votos || '0') ? e : b),
    pastElections[0],
  );
  const lastV = parseInt(pastElections[pastElections.length - 1]?.votos || '0');
  const projN = parseInt(proj || '0');
  const delta =
    lastV > 0 && targetMetaN > 0 ? Math.round(((targetMetaN - lastV) / lastV) * 100) : null;

  const isDirty =
    JSON.stringify(elections) !== snap.current ||
    proj !== (city.projecao_votos?.toString() ?? '');

  // ── popover ───────────────────────────────────────────────────────────────
  const openPop = (ano: number, ev: React.MouseEvent) => {
    if (!canEdit) return;
    const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    const ex = elections.find(x => x.ano === ano);
    setPop({
      ano,
      meta: ano === targetYear ? targetMeta : (ex?.meta ?? ''),
      result: ano === targetYear ? '' : (ex?.votos ?? ''),
      x: rect.left + rect.width / 2,
      y: rect.bottom + 10,
    });
  };

  const savePop = () => {
    if (!pop) return;
    if (pop.ano === targetYear) {
      if (currentCampaign) {
        // update the campaign entry's meta in the elections list
        setElections(prev =>
          prev.map(x => x.ano === targetYear ? { ...x, meta: pop.meta } : x)
        );
      } else {
        setProj(pop.meta);
      }
    } else {
      setElections(prev => {
        const idx = prev.findIndex(x => x.ano === pop.ano);
        if (idx === -1) return [...prev, { ano: pop.ano, votos: pop.result, meta: pop.meta }];
        return prev.map((x, i) => (i === idx ? { ...x, votos: pop.result, meta: pop.meta } : x));
      });
    }
    setPop(null);
  };

  const popStatus = (): 'ok' | 'below' | 'none' => {
    if (!pop) return 'none';
    const m = parseInt(pop.meta || '0');
    const r = parseInt(pop.result || '0');
    if (!m) return 'none';
    return r >= m ? 'ok' : 'below';
  };

  // ── save elections ────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const history: CityElectionResult[] = elections.map(e => ({
        ano: e.ano,
        votos_obtidos: parseInt(e.votos) || 0,
        meta_votos: e.meta ? parseInt(e.meta) : null,
      }));
      const origYears = (JSON.parse(snap.current) as Array<{ ano: number }>).map(e => e.ano);
      const deleted = origYears.filter(y => !elections.map(e => e.ano).includes(y));
      await onSave(city.id, {
        electionHistory: history,
        projecao_votos: projN || undefined,
        ...(deleted.length > 0 && { deletedElectionYears: deleted }),
      });
      snap.current = JSON.stringify(elections);
    } finally {
      setSaving(false);
    }
  };

  // ── comment ───────────────────────────────────────────────────────────────
  const sendComment = async () => {
    if (!comment.trim() || !onAddRegistro) return;
    setSending(true);
    try {
      await onAddRegistro(city.id, comment.trim());
      setComment('');
    } finally {
      setSending(false);
    }
  };

  // ── responsible ───────────────────────────────────────────────────────────
  const assignResp = async (uid: number) => {
    if (!onAssignResponsible) return;
    setAssigningResp(true);
    setShowResp(false);
    try { await onAssignResponsible(city.id, uid); }
    finally { setAssigningResp(false); }
  };

  // ── notes ─────────────────────────────────────────────────────────────────
  const notesBlur = async () => {
    setEditNotes(false);
    const text = notesRef.current?.innerText ?? '';
    if (text !== (city.notas_estrategicas ?? '')) {
      await onSave(city.id, { notas_estrategicas: text });
    }
  };

  // ── filtered feed ─────────────────────────────────────────────────────────
  const ibge = city.ibge_id?.toString() ?? '—';

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <>
    <CityModalMobile
      isOpen={isOpen}
      onClose={onClose}
      city={city}
      teamMembers={teamMembers}
      onSave={onSave}
      onAssignResponsible={onAssignResponsible}
      onScheduleVisit={onScheduleVisit}
      onNewDemand={onNewDemand}
      onExportReport={onExportReport}
      onViewAllContacts={onViewAllContacts}
      onAddRegistro={onAddRegistro}
      onAddMultiplicador={onAddMultiplicador}
      onVincularContato={onVincularContato}
      onDemandClick={onDemandClick}
      demandPanel={demandPanel}
      registros={registros}
      demandas={demandas}
      contatos={contatos}
    />
    <Overlay $open={isOpen} onClick={onClose}>
      <Modal $open={isOpen} onClick={e => e.stopPropagation()}>

        {/* ── Header ────────────────────────────────────────────────────────── */}
        <Header>
          <Breadcrumb>
            <span>Cidade</span>
            <Dot />
            <Tag>#CID-{ibge}</Tag>
            {city.regiao && <><Dot /><span style={{ textTransform: 'none', letterSpacing: 0 }}>Região {city.regiao}</span></>}
            {city.mesorregiao && <><Dot /><span style={{ textTransform: 'none', letterSpacing: 0 }}>{city.mesorregiao}</span></>}
            {city.populacao && <><Dot /><span style={{ textTransform: 'none', letterSpacing: 0 }}>{fmtN(city.populacao)} hab.</span></>}
          </Breadcrumb>

          <HeaderRow>
            <TitleArea>
              <TitleDisplay>
                {city.nome}
                <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: 15, marginLeft: 8 }}>
                  {city.uf}
                </span>
              </TitleDisplay>
            </TitleArea>

            <Actions>
              {onScheduleVisit && (
                <Btn $v="secondary" onClick={() => onScheduleVisit(city.id)}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M16 2v4M8 2v4M3 10h18" />
                  </svg>
                  Agendar visita
                </Btn>
              )}

              {onNewDemand && (
                <Btn $v="primary" onClick={() => onNewDemand(city.id)}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path d="M12 4v16m8-8H4" strokeLinecap="round" />
                  </svg>
                  Nova demanda
                </Btn>
              )}

              <MoreWrap ref={moreRef}>
                <Btn $v="ghost" onClick={() => setShowMore(p => !p)} title="Mais opções">
                  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="5" cy="12" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="19" cy="12" r="2" />
                  </svg>
                </Btn>
                {showMore && (
                  <MoreDrop>
                    {onExportReport && (
                      <MoreItem onClick={() => { setShowMore(false); onExportReport(city.id); }}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v2a2 2 0 002 2h14a2 2 0 002-2v-2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Exportar relatório
                      </MoreItem>
                    )}
                    {onViewAllContacts && (
                      <MoreItem onClick={() => { setShowMore(false); onViewAllContacts(city.id); }}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                        </svg>
                        Ver todos os contatos
                      </MoreItem>
                    )}
                  </MoreDrop>
                )}
              </MoreWrap>

              <CloseBtn onClick={onClose} title="Fechar">×</CloseBtn>
            </Actions>
          </HeaderRow>
        </Header>

        {/* ── Body ──────────────────────────────────────────────────────────── */}
        <Body>

          {/* Left column */}
          <LeftCol>

            {/* Vote History */}
            {canView && (
              <Card>
                <CardHead>
                  <div>
                    <CardLabel>Histórico de Votos</CardLabel>
                    <CardSub>
                      {elections.length > 0
                        ? `${elections.length} eleição${elections.length !== 1 ? 'ões' : ''} · ${range} · ${metasBatidas}/${withMeta} metas batidas${peak?.votos ? ` · pico em ${peak.ano}` : ''}`
                        : 'Nenhuma eleição — clique nas colunas para adicionar'}
                    </CardSub>
                  </div>
                  {canEdit && isDirty && (
                    <Btn $v="primary" onClick={handleSave} disabled={saving}>
                      {saving ? <Spinner /> : 'Salvar'}
                    </Btn>
                  )}
                </CardHead>

                <CardBody>
                  <Legend>
                    <LegendItem><Swatch $k="blue" />Realizado</LegendItem>
                    <LegendItem><Swatch $k="gray" />Meta da época</LegendItem>
                    <LegendItem><Swatch $k="amber" />Meta 2026</LegendItem>
                  </Legend>

                  <ChartScroll>
                    {pastElections.map(el => {
                      const r = parseInt(el.votos || '0');
                      const m = parseInt(el.meta || '0');
                      const bateu = m > 0 && r >= m;
                      return (
                        <ChartCol
                          key={el.ano}
                          $click={canEdit}
                          onClick={e => openPop(el.ano, e)}
                          title={canEdit ? `Editar ${el.ano}` : undefined}
                        >
                          <Bars>
                            {el.meta ? (
                              <BarWrap>
                                <BarVal $k="meta">{fmtN(m)}</BarVal>
                                <Bar $h={toH(el.meta)} $k="meta" title={`Meta: ${fmtN(m)}`} />
                              </BarWrap>
                            ) : (
                              <BarWrap>
                                <NoMetaSlot title="Sem meta — clique para adicionar">+ meta</NoMetaSlot>
                              </BarWrap>
                            )}
                            <BarWrap>
                              <BarVal $k="result">{r > 0 ? fmtN(r) : ''}</BarVal>
                              <Bar $h={toH(el.votos)} $k="result" title={`Resultado: ${fmtN(r)}`} />
                            </BarWrap>
                          </Bars>
                          <ColLabel>
                            {el.ano}
                            <ColStatus $ok={m > 0 ? bateu : null}>
                              {m > 0 ? (bateu ? '✓' : '✗') : '–'}
                            </ColStatus>
                          </ColLabel>
                        </ChartCol>
                      );
                    })}

                    {/* Current campaign / target column */}
                    <ChartCol
                      $click={canEdit}
                      onClick={e => openPop(targetYear, e)}
                      title={canEdit ? `Editar Meta ${targetYear}` : undefined}
                    >
                      <Bars>
                        <BarWrap>
                          <BarVal $k="proj">{targetMetaN > 0 ? fmtN(targetMetaN) : ''}</BarVal>
                          <Bar $h={toH(targetMeta)} $k="proj" title={`Meta ${targetYear}: ${fmtN(targetMetaN || null)}`} />
                        </BarWrap>
                      </Bars>
                      <ColLabel>
                        {targetYear}
                        <ColStatus $ok={null}>META</ColStatus>
                      </ColLabel>
                    </ChartCol>
                  </ChartScroll>

                  <ChartFoot
                    $click={canEdit}
                    onClick={canEdit ? e => openPop(targetYear, e as any) : undefined}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#9ca3af', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: 'linear-gradient(180deg,#F5C44C,#E8A33B)', display: 'inline-block', flexShrink: 0 }} />
                      Meta {targetYear}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                      <strong style={{ font: '700 16px Manrope,sans-serif', letterSpacing: '-0.01em', color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
                        {targetMetaN ? fmtN(targetMetaN) : '—'}
                      </strong>
                      <span style={{ fontSize: 11.5, color: '#9ca3af', fontWeight: 500 }}>votos</span>
                    </span>
                    {delta !== null && (
                      <span style={{ fontWeight: 700, fontSize: 12, padding: '3px 8px', borderRadius: 99, background: delta >= 0 ? '#d1fae5' : '#fee2e2', color: delta >= 0 ? '#065f46' : '#991b1b', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {delta >= 0 ? '+' : ''}{delta}% vs {elections[elections.length - 1]?.ano ?? targetYear - 4}
                      </span>
                    )}
                    {canEdit && (
                      <span className="chf-edit" style={{ opacity: 0, transition: 'opacity .15s', fontSize: 11.5, color: '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 500 }}>
                        <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                        clique para editar
                      </span>
                    )}
                  </ChartFoot>
                </CardBody>
              </Card>
            )}

            {/* City Info */}
            <div>
              <SectionEyebrow>Sobre a cidade</SectionEyebrow>
              <FieldsGrid>
                <Field>
                  <FieldLbl>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path d="M5 21V4h11l-2 4 2 4H5"/></svg>
                    UF
                  </FieldLbl>
                  <FieldVal><UFBadge>{city.uf}</UFBadge></FieldVal>
                </Field>
                <Field>
                  <FieldLbl>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12z"/><circle cx="12" cy="9" r="2.5"/></svg>
                    Região
                  </FieldLbl>
                  <FieldVal>{city.regiao ?? '—'}</FieldVal>
                </Field>
                <Field>
                  <FieldLbl>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12z"/><circle cx="12" cy="9" r="2.5"/></svg>
                    Mesorregião
                  </FieldLbl>
                  <FieldVal>{city.mesorregiao ?? '—'}</FieldVal>
                </Field>
                <Field>
                  <FieldLbl>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><circle cx="9" cy="9" r="3.5"/><path d="M2.5 19c1-3 3.5-4.5 6.5-4.5s5.5 1.5 6.5 4.5"/><path d="M16 11.5a3.5 3.5 0 1 0 0-7"/><path d="M22 19c-.6-1.8-1.9-3-3.5-3.7"/></svg>
                    Habitantes
                  </FieldLbl>
                  <FieldVal>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtN(city.populacao)}</span>
                    {city.populacao_ano && <span style={{ color: '#9ca3af', fontSize: 12 }}>({city.populacao_ano})</span>}
                  </FieldVal>
                </Field>
                <Field>
                  <FieldLbl>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M8 12l3 3 5-6"/></svg>
                    Eleitores aptos
                  </FieldLbl>
                  <FieldVal>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtN(city.populacao_votantes)}</span>
                    {city.populacao && city.populacao_votantes ? (
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>· {((city.populacao_votantes / city.populacao) * 100).toFixed(0)}% da população</span>
                    ) : null}
                  </FieldVal>
                </Field>
                <Field>
                  <FieldLbl>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><rect x="7" y="4" width="10" height="4" rx="1"/><path d="M7 6H5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-2"/></svg>
                    Cód. IBGE
                  </FieldLbl>
                  <FieldVal>
                    <span style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: '12.5px', color: '#6b7280' }}>{ibge}</span>
                  </FieldVal>
                </Field>
              </FieldsGrid>
            </div>

            {/* Local Team */}
            <div>
              <SectionEyebrow>Equipe local</SectionEyebrow>
              <FieldsGrid>
                <Field>
                  <FieldLbl>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M4 21c1.5-4 5-6 8-6s6.5 2 8 6"/></svg>
                    Responsável
                  </FieldLbl>
                  <FieldVal>
                    {isAdmin ? (
                      <div ref={respRef} style={{ position: 'relative', display: 'inline-block' }}>
                        <RespBtn
                          $has={!!city.responsavel}
                          onClick={() => setShowResp(p => !p)}
                          disabled={assigningResp}
                        >
                          {assigningResp ? (
                            <Spinner />
                          ) : city.responsavel ? (
                            <UserAvatar name={city.responsavel.nome} fotoUrl={city.responsavel.foto_url} size={18} />
                          ) : (
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                          )}
                          {assigningResp ? 'Atribuindo...' : city.responsavel?.nome ?? 'Sem responsável'}
                          <span style={{ fontSize: 9 }}>▾</span>
                        </RespBtn>

                        {showResp && (
                          <RespDrop>
                            <div style={{ padding: '7px 9px', borderBottom: '1px solid #e5e7eb' }}>
                              <input
                                autoFocus
                                type="text"
                                placeholder="Buscar..."
                                value={respQ}
                                onChange={e => setRespQ(e.target.value)}
                                onClick={e => e.stopPropagation()}
                                style={{
                                  width: '100%', padding: '5px 9px', border: '1px solid #e5e7eb',
                                  borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box',
                                }}
                              />
                            </div>
                            <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                              {teamMembers
                                .filter(m => m.nome.toLowerCase().includes(respQ.toLowerCase()))
                                .map(m => (
                                  <RespOpt key={m.id} onClick={() => assignResp(m.id)}>
                                    <UserAvatar name={m.nome} fotoUrl={m.foto_url} size={24} />
                                    {m.nome}
                                  </RespOpt>
                                ))}
                            </div>
                          </RespDrop>
                        )}
                      </div>
                    ) : city.responsavel ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <UserAvatar name={city.responsavel.nome} fotoUrl={city.responsavel.foto_url} size={20} />
                        {city.responsavel.nome}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: 13 }}>Sem responsável</span>
                    )}
                  </FieldVal>
                </Field>

                <Field>
                  <FieldLbl>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/></svg>
                    Última visita
                  </FieldLbl>
                  <FieldVal>
                    <span style={{ color: city.ultima_visita ? '#111827' : '#9ca3af', fontSize: 14 }}>
                      {fmtD(city.ultima_visita) === '—' ? 'Não registrada' : fmtD(city.ultima_visita)}
                    </span>
                  </FieldVal>
                </Field>
              </FieldsGrid>

              {/* Multiplicadores — full-width field outside the rounded grid */}
              <Field style={{ alignItems: 'start', minHeight: 'auto', padding: '10px 8px 14px 4px', borderBottom: 'none' }}>
                <FieldLbl style={{ paddingTop: 4 }}>
                  <span style={{ fontSize: 16, color: '#f59e0b', lineHeight: 1 }}>★</span>
                  Multiplicadores
                </FieldLbl>
                <FieldVal style={{ display: 'block' } as any}>
                  <MultList>
                    {(city.multiplicadores ?? []).map(m => (
                      <MultChip key={m.id}>
                        <UserAvatar name={m.nome} fotoUrl={m.foto_url} size={18} />
                        {m.nome}
                      </MultChip>
                    ))}
                    {!city.multiplicadores?.length && (
                      <span style={{ color: '#9ca3af', fontSize: 13, marginLeft: 4 }}>Nenhum</span>
                    )}
                  </MultList>
                </FieldVal>
              </Field>
            </div>

            {/* Strategic Notes */}
            <div>
              <SectionEyebrow>Notas estratégicas</SectionEyebrow>
              <NotesEditable
                ref={notesRef}
                contentEditable
                suppressContentEditableWarning
                data-placeholder="Quem manda na cidade? Quais são as portas de entrada? Eventos-chave?"
                onFocus={() => setEditNotes(true)}
                onBlur={notesBlur}
                onKeyDown={e => {
                  if (e.key === 'Escape') (e.target as HTMLElement).blur();
                }}
              >
                {city.notas_estrategicas ?? ''}
              </NotesEditable>
            </div>

            <Danger>
              <DangerTxt>
                {city.data_importacao
                  ? <>Importada do TSE em {fmtD(city.data_importacao)} · IBGE <strong style={{ color: '#6b7280' }}>{ibge}</strong></>
                  : <>IBGE <strong style={{ color: '#6b7280' }}>{ibge}</strong></>}
              </DangerTxt>
            </Danger>
          </LeftCol>

          {/* Right column */}
          <RightCol>
            <PaneTabs role="tablist">
              {canViewComments && (
                <PaneTabBtn $a={rtab === 'comentarios'} role="tab" aria-selected={rtab === 'comentarios'} onClick={() => setRtab('comentarios')}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path d="M5 4h11l3 3v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z"/><path d="M8 11h8M8 15h5"/></svg>
                  Comentários
                  {registros.length > 0 && <PaneTabCount>{registros.length}</PaneTabCount>}
                </PaneTabBtn>
              )}
              <PaneTabBtn $a={rtab === 'demandas'} role="tab" aria-selected={rtab === 'demandas'} onClick={() => setRtab('demandas')}>
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><rect x="7" y="4" width="10" height="4" rx="1"/><path d="M7 6H5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-2"/></svg>
                Demandas
                {demandas.length > 0 && <PaneTabCount>{demandas.length}</PaneTabCount>}
              </PaneTabBtn>
              <PaneTabBtn $a={rtab === 'contatos'} role="tab" aria-selected={rtab === 'contatos'} onClick={() => setRtab('contatos')}>
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><circle cx="9" cy="9" r="3.5"/><path d="M2.5 19c1-3 3.5-4.5 6.5-4.5s5.5 1.5 6.5 4.5"/><path d="M16 11.5a3.5 3.5 0 1 0 0-7"/><path d="M22 19c-.6-1.8-1.9-3-3.5-3.7"/></svg>
                Contatos
                {contatos.length > 0 && <PaneTabCount>{contatos.length}</PaneTabCount>}
              </PaneTabBtn>
            </PaneTabs>

            <RBody>

              {/* Comentários tab */}
              {canViewComments && rtab === 'comentarios' && (
                <>
                  <RegHead>
                    <RegCount>{loadingRegistros ? '…' : `${registros.length} comentário${registros.length !== 1 ? 's' : ''}`}</RegCount>
                  </RegHead>

                  <RegFeed>
                    {loadingRegistros ? (
                      [1,2,3].map(i => (
                        <ShimmerRow key={i}>
                          <ShimmerCircle />
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <ShimmerBar $w="60%" />
                            <ShimmerBar $w="85%" $h="11px" />
                          </div>
                        </ShimmerRow>
                      ))
                    ) : registros.length === 0 ? (
                      <Empty>Nenhum comentário ainda</Empty>
                    ) : (
                      registros.map(r => {
                        const autor = r.autor ?? r.usuario;
                        const texto = r.texto ?? r.descricao_atualizacao ?? '';
                        const data = r.data ?? r.created_at;
                        return (
                          <EvItem key={r.id}>
                            <EvRail>
                              <EvDot $comment>
                                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path d="M5 4h11l3 3v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z"/><path d="M8 11h8M8 15h5"/></svg>
                              </EvDot>
                            </EvRail>
                            <EvBody>
                              <EvHeader>
                                <EvWho>{autor?.nome ?? 'Sistema'}</EvWho>
                                <span style={{ color: '#6b7280' }}>comentou</span>
                                <EvTime>{ago(data)}</EvTime>
                              </EvHeader>
                              <EvBubble>{texto}</EvBubble>
                            </EvBody>
                          </EvItem>
                        );
                      })
                    )}
                  </RegFeed>

                  <ComposerForm onSubmit={e => { e.preventDefault(); sendComment(); }}>
                    <ComposerBox>
                      <ComposerInput
                        placeholder="Anote uma conversa, lembrete ou observação…"
                        value={comment}
                        rows={1}
                        onChange={e => setComment(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            sendComment();
                          }
                        }}
                      />
                      <ComposerRow>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
                          <Btn
                            $v="primary"
                            type="submit"
                            disabled={!comment.trim() || sending || !onAddRegistro || !canComment}
                            style={{ height: 28, padding: '0 11px', fontSize: 12, opacity: (!comment.trim() || sending || !onAddRegistro || !canComment) ? 0.5 : 1 }}
                          >
                            {sending ? <Spinner /> : (
                              <>
                                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path d="M5 12l16-8-6 18-3-7-7-3z"/></svg>
                                Registrar
                              </>
                            )}
                          </Btn>
                        </div>
                      </ComposerRow>
                    </ComposerBox>
                  </ComposerForm>
                </>
              )}

              {/* Demandas tab */}
              {rtab === 'demandas' && (
                <>
                  <div style={{ flex: 1, paddingTop: 8 }}>
                    {loadingDemandas ? (
                      [1,2,3,4].map(i => (
                        <ShimmerRow key={i}>
                          <ShimmerBar $w="4px" $h="40px" style={{ borderRadius: 2, flexShrink: 0 } as any} />
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <ShimmerBar $w="70%" />
                            <ShimmerBar $w="45%" $h="11px" />
                          </div>
                        </ShimmerRow>
                      ))
                    ) : demandas.length === 0 ? (
                      <Empty>Nenhuma demanda vinculada</Empty>
                    ) : (
                      demandas.map(d => (
                        <DCard key={d.id} onClick={() => onDemandClick?.(d)} style={onDemandClick ? { cursor: 'pointer' } : undefined}>
                          <DStripe $s={d.status} />
                          <DInfo>
                            <DTitle>{d.titulo}</DTitle>
                            <DMeta>
                              <StatusPill $s={d.status}>{SL[d.status] ?? d.status}</StatusPill>
                              <span>#{d.id}</span>
                              {d.solicitante && <span>{d.solicitante.nome}</span>}
                              <span>{ago(d.data_cadastro)}</span>
                            </DMeta>
                          </DInfo>
                        </DCard>
                      ))
                    )}
                  </div>
                  {onNewDemand && (
                    <TabAction onClick={() => onNewDemand(city.id)}>
                      <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path d="M12 4v16m8-8H4" strokeLinecap="round" />
                      </svg>
                      Nova demanda para esta cidade
                    </TabAction>
                  )}
                </>
              )}

              {/* Contatos tab */}
              {rtab === 'contatos' && (
                <>
                  <div style={{ flex: 1 }}>
                    {loadingContatos ? (
                      [1,2,3,4,5].map(i => (
                        <ShimmerRow key={i}>
                          <ShimmerCircle />
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <ShimmerBar $w="55%" />
                            <ShimmerBar $w="35%" $h="11px" />
                          </div>
                        </ShimmerRow>
                      ))
                    ) : contatos.length === 0 ? (
                      <Empty>Nenhum contato cadastrado</Empty>
                    ) : (
                      contatos.map(c => (
                        <CRow key={c.id}
                          onClick={() => { setOpenContatoId(c.id); }}
                          style={{ cursor: 'pointer' }}
                        >
                          <UserAvatar name={c.nome} fotoUrl={c.foto_url} size={32} />
                          <CInfo>
                            <CName>
                              {c.nome}
                              {c.is_multiplicador && (
                                <span title="Multiplicador" style={{ color: '#f59e0b', fontSize: 12 }}>★</span>
                              )}
                            </CName>
                            {c.cargo && <CCargo>{c.cargo}</CCargo>}
                          </CInfo>
                          <HoverActs className="ha">
                            {c.whatsapp && (
                              <IBtn
                                as="a"
                                href={`https://wa.me/${c.whatsapp.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="WhatsApp"
                              >
                                <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                              </IBtn>
                            )}
                            <IBtn title="Ligar">
                              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                            </IBtn>
                          </HoverActs>
                        </CRow>
                      ))
                    )}
                  </div>

                </>
              )}

            </RBody>
          </RightCol>
        </Body>

        {/* ── Demand slide-in panel ──────────────────────────────────────── */}
        {demandPanel && (demandPanel.isOpen || isPanelClosing) && (
          <DemandSlidePanel $closing={isPanelClosing}>
            <DemandDetailModal
              isOpen={true}
              demand={demandPanel.demand as any}
              onClose={handleDemandPanelClose}
              onUpdate={() => {}}
              contained
              registros={demandPanel.registros}
              loadingRegistros={demandPanel.loadingRegistros}
              newRegistro={demandPanel.newRegistro}
              onNewRegistroChange={demandPanel.onNewRegistroChange}
              onAddRegistro={demandPanel.onAddRegistro}
              addingRegistro={demandPanel.addingRegistro}
              editingRegistro={demandPanel.editingRegistro}
              editingText={demandPanel.editingText}
              onEditRegistro={demandPanel.onEditRegistro}
              onSaveEdit={demandPanel.onSaveEdit}
              onCancelEdit={demandPanel.onCancelEdit}
              onEditTextChange={demandPanel.onEditTextChange}
              onDeleteRegistro={demandPanel.onDeleteRegistro}
              savingRegistroId={demandPanel.savingRegistroId}
              deletingRegistroId={demandPanel.deletingRegistroId}
              openMenuId={demandPanel.openMenuId}
              onMenuToggle={demandPanel.onMenuToggle}
              canViewUpdates={demandPanel.canViewUpdates}
              canCreateUpdates={demandPanel.canCreateUpdates}
              canEditUpdate={demandPanel.canEditUpdate}
              canDeleteUpdate={demandPanel.canDeleteUpdate}
              canEditDemand={demandPanel.canEditDemand}
              canDeleteDemand={demandPanel.canDeleteDemand}
              canAssignResponsible={demandPanel.canAssignResponsible}
              canUnassignResponsible={demandPanel.canUnassignResponsible}
              historico={demandPanel.historico}
              loadingHistorico={demandPanel.loadingHistorico}
            />
          </DemandSlidePanel>
        )}
        {/* ── Contact slide-in panel ──────────────────────────────────────────── */}
        {(openContatoId !== null || isContactPanelClosing) && (
          <DemandSlidePanel $closing={isContactPanelClosing}>
            <ContactDetailPanel
              contatoId={openContatoId}
              onClose={handleContatoPanelClose}
            />
          </DemandSlidePanel>
        )}
      </Modal>

      {/* ── Popover (fixed, outside modal) ─────────────────────────────────── */}
      {pop && canEdit && (
        <Popover
          ref={popRef}
          style={{
            left: Math.min(pop.x - 112, window.innerWidth - 240),
            top: Math.min(pop.y, window.innerHeight - 240),
          }}
          onClick={e => e.stopPropagation()}
        >
          <PopTitle>
            <span>{pop.ano === TARGET ? 'Meta 2026' : `Eleição ${pop.ano}`}</span>
            {pop.ano !== TARGET && (
              <PopBadge $s={popStatus()}>
                {popStatus() === 'ok' ? '✓ bateu' : popStatus() === 'below' ? '✗ abaixo' : 'sem meta'}
              </PopBadge>
            )}
          </PopTitle>

          {pop.ano !== TARGET && (
            <PopField>
              <PopLabel>Resultado</PopLabel>
              <PopInput
                autoFocus
                type="text"
                inputMode="numeric"
                placeholder="Votos obtidos"
                value={pop.result}
                onChange={e => {
                  if (/^\d*$/.test(e.target.value))
                    setPop(p => p ? { ...p, result: e.target.value } : null);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') savePop();
                  if (e.key === 'Escape') setPop(null);
                }}
              />
            </PopField>
          )}

          <PopField>
            <PopLabel>{pop.ano === TARGET ? 'Projeção de votos' : 'Meta'}</PopLabel>
            <PopInput
              autoFocus={pop.ano === TARGET}
              type="text"
              inputMode="numeric"
              placeholder="Votos meta"
              value={pop.meta}
              onChange={e => {
                if (/^\d*$/.test(e.target.value))
                  setPop(p => p ? { ...p, meta: e.target.value } : null);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') savePop();
                if (e.key === 'Escape') setPop(null);
              }}
            />
          </PopField>

          <PopActions>
            <PopBtn onClick={() => setPop(null)}>Cancelar</PopBtn>
            <PopBtn $primary onClick={savePop}>Salvar</PopBtn>
          </PopActions>
        </Popover>
      )}
    </Overlay>
    </>
  );
};
